import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  FileText, MessageSquare, CheckSquare, Shield, Upload, AlertTriangle,
  Pencil, Mail, XCircle, ChevronRight, ChevronDown, Bell, Check, Activity, User, Clock, CalendarPlus
} from 'lucide-react';
import { AskOltaAIGlobal } from '../components/AskOltaAI';

const ACTION_CONFIG = {
  note_added: { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  checklist_checked: { icon: CheckSquare, color: 'text-green-400', bg: 'bg-green-500/10' },
  checklist_unchecked: { icon: XCircle, color: 'text-muted-foreground', bg: 'bg-secondary' },
  scores_overridden: { icon: Pencil, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  document_uploaded: { icon: Upload, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  deal_passed: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
  broker_requested: { icon: Mail, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  meeting_agenda_added: { icon: CalendarPlus, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  checkin_requested: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
};
const DEFAULT_CFG = { icon: FileText, color: 'text-muted-foreground', bg: 'bg-secondary' };

export default function ActivityFeedPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);

  const fetchActivity = async () => {
    try {
      const res = await api.getGlobalActivity();
      setData(res.data);
      // Mark as seen
      await api.markActivitySeen();
    } catch { toast.error('Failed to load activity'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchActivity(); }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!data) return null;

  const { projects, total_unread, since_last_login } = data;
  const hasSinceLogin = since_last_login && since_last_login.length > 0;
  const totalNewEntries = hasSinceLogin ? since_last_login.reduce((s, u) => s + u.entries.length, 0) : 0;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="activity-feed">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Activity Feed</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {total_unread > 0 ? <span className="text-blue-400 font-medium">{total_unread} new update{total_unread > 1 ? 's' : ''}</span> : 'All caught up'} across {projects.length} deal{projects.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Ask OLTA AI — global, above the feed */}
      <AskOltaAIGlobal />

      {/* ─── Since Your Last Login ─── */}
      {hasSinceLogin && (
        <div className="border border-blue-600/20 rounded-sm bg-blue-950/10 overflow-hidden" data-testid="since-last-login">
          <div className="px-4 py-3 border-b border-blue-600/15 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            <p className="font-heading text-sm text-blue-400">Since Your Last Login</p>
            <span className="text-[10px] font-mono text-blue-400/60 ml-auto">{totalNewEntries} change{totalNewEntries !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-blue-600/10">
            {since_last_login.map(u => (
              <div key={u.user_id} className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-[10px] font-bold font-mono">
                    {u.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <p className="text-sm font-medium text-foreground">{u.user_name}</p>
                  <span className="text-[10px] text-muted-foreground font-mono">{u.entries.length} update{u.entries.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-1 ml-8">
                  {u.entries.slice(0, 10).map((e, i) => {
                    const cfg = ACTION_CONFIG[e.action] || DEFAULT_CFG;
                    const Icon = cfg.icon;
                    return (
                      <div key={e.id || i} className="flex items-start gap-2 py-0.5">
                        <div className={`w-5 h-5 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Icon className={`w-3 h-3 ${cfg.color}`} strokeWidth={1.5} />
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                          <button onClick={() => navigate(e.project_id === 'team-development' ? '/team-development' : `/projects/${e.project_id}`)} className="text-foreground font-medium hover:text-blue-400 transition-colors">{e.project_name}</button>
                          {e.project_id === 'team-development' && e.action ? <> <span className="text-foreground/90">{e.action}</span> —</> : ''}
                          {' '}{e.detail}
                        </p>
                        <span className="text-[9px] text-muted-foreground/50 font-mono shrink-0 mt-0.5">
                          {new Date(e.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    );
                  })}
                  {u.entries.length > 10 && (
                    <p className="text-[10px] text-muted-foreground/50 ml-7">+ {u.entries.length - 10} more</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Full Activity by Deal ─── */}

      {projects.length === 0 ? (
        <div className="text-center py-20 border border-border rounded-sm bg-card">
          <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No activity yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map(p => {
            const isExpanded = expandedProject === p.project_id;
            return (
              <div key={p.project_id} className="border border-border rounded-sm bg-card overflow-hidden">
                {/* Project header */}
                <div role="button" tabIndex={0}
                  onClick={() => setExpandedProject(isExpanded ? null : p.project_id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedProject(isExpanded ? null : p.project_id); } }}
                  className="w-full flex items-center justify-between p-3 hover:bg-secondary/30 transition-colors text-left cursor-pointer select-none">
                  <div className="flex items-center gap-3 min-w-0">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{p.project_name}</span>
                        {p.unread > 0 && (
                          <span className="flex items-center gap-1 text-[10px] font-mono text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded px-1.5 py-0.5">
                            <Bell className="w-3 h-3" /> {p.unread} new
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.entries.length} update{p.entries.length !== 1 ? 's' : ''} · Last: {new Date(p.latest_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(p.project_id === 'team-development' ? '/team-development' : `/projects/${p.project_id}`); }}
                    className="text-xs text-muted-foreground hover:text-foreground h-7 px-2 shrink-0">
                    {p.project_id === 'team-development' ? 'Öffnen' : 'Open Deal'} <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>

                {/* Expanded: activity entries */}
                {isExpanded && (
                  <div className="border-t border-border px-3 py-2 space-y-1 max-h-[400px] overflow-y-auto bg-secondary/10">
                    {p.entries.map(e => {
                      const cfg = ACTION_CONFIG[e.action] || DEFAULT_CFG;
                      const Icon = cfg.icon;
                      const isNew = e.at > (data.last_seen || '') && e.user_id !== data.current_user_id;
                      return (
                        <div key={e.id} className={`flex items-start gap-2.5 py-1.5 rounded-sm px-1 ${isNew ? 'bg-blue-500/5' : ''}`}>
                          <div className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                            <Icon className={`w-3 h-3 ${cfg.color}`} strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {p.project_id === 'team-development' && e.action
                                ? <><span className="text-foreground font-medium">{e.action}</span> — {e.detail}</>
                                : <><span className="text-foreground font-medium">{e.user_name}</span> {e.detail}</>}
                            </p>
                          </div>
                          <span className="text-[9px] text-muted-foreground font-mono shrink-0 mt-0.5">
                            {new Date(e.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Compact activity feed for deal detail page
export function DealActivityFeed({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getProjectActivity(projectId).then(res => setEntries(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  if (loading || entries.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <p className="font-heading text-base text-foreground mb-3">Activity Log ({entries.length})</p>
      <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
        {entries.slice(0, 30).map(e => {
          const cfg = ACTION_CONFIG[e.action] || DEFAULT_CFG;
          const Icon = cfg.icon;
          return (
            <div key={e.id} className="flex items-start gap-2.5 py-1">
              <div className={`w-5 h-5 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                <Icon className={`w-3 h-3 ${cfg.color}`} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-medium">{e.user_name}</span> {e.detail}
                </p>
              </div>
              <span className="text-[9px] text-muted-foreground font-mono shrink-0">
                {new Date(e.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
